var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Box } from "@gemini/core";
import { HouseFilledIcon, LocationArrowIcon, LocationDotFilledIcon, } from "@gemini/icons";
import { Autocomplete, LoadingState, TextButton } from "@gemini/ui";
import { useBrowserPosition } from "@/hooks"; // move to location hooks
import { useDebounce } from "react-use";
import { useForm, Controller } from "react-hook-form";
import { useI18n } from "@/shared/hooks";
import { Address } from "@/location/components";
import { useTrackingEvent } from "@/hooks/tracking/useTrackingEvent";
import { useClassified } from "@/contexts/classified";
import { useMapContext } from "../../../contexts/MapContext";
import { TravelTimeIndicators } from "./TravelTimeIndicators";
import { useGeoSearch } from "../../../hooks/useGeoSearch";
import { useTravelTimes } from "../../../hooks/useTravelTimes";
export const TravelTimeCalculator = () => {
    var _a, _b;
    const { searchLocations } = useGeoSearch();
    const { t } = useI18n();
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState([]);
    const { travelTimes, loadingState, resetTravelTime, setDestination } = useMapContext();
    const { av_distribution_type_global } = (_b = (_a = useClassified()) === null || _a === void 0 ? void 0 : _a.tracking) !== null && _b !== void 0 ? _b : {};
    const { control, setValue, watch } = useForm({
        defaultValues: { searchTerm: "" },
    });
    const searchTerm = watch("searchTerm");
    const EMPTY_STATE = searchTerm
        ? {
            title: t("location.map.modal.travel_times.autocomplete.empty_state.title"),
            description: t("location.map.modal.travel_times.autocomplete.empty_state.description"),
        }
        : undefined;
    const { getUserPosition } = useBrowserPosition();
    const { calculateTravelTimes, isDistanceTooFar } = useTravelTimes();
    useDebounce(() => __awaiter(void 0, void 0, void 0, function* () {
        if ((searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.length) < 3) {
            setResults([]);
            return;
        }
        const results = yield searchLocations(searchTerm);
        setIsLoading(false);
        setResults(results);
    }), 500, [searchTerm]);
    // Define the event data for tracking
    const EVENT_DATA = {
        event: "travel_time_estimation_submit",
        av_pagetitle: "classified_detail_map",
        av_category: "classified",
        av_distribution_type_global,
    };
    const trackTravelTimeSearch = useTrackingEvent(EVENT_DATA);
    // reactThor need to be updated with this event
    const handleSelectionChange = (_value, key) => {
        if (!key)
            return;
        const selectedLocation = results.find((item) => item.id === key);
        if (selectedLocation) {
            setDestination(selectedLocation);
            calculateTravelTimes(selectedLocation);
            trackTravelTimeSearch();
        }
    };
    const handleBrowserLocation = () => __awaiter(void 0, void 0, void 0, function* () {
        const browserLocationLabel = t("location.map.modal.travel_times.position.label");
        setValue("searchTerm", browserLocationLabel);
        const userLocation = yield getUserPosition();
        const selectedLocation = {
            coordinates: {
                lat: userLocation === null || userLocation === void 0 ? void 0 : userLocation.latitude,
                lng: userLocation === null || userLocation === void 0 ? void 0 : userLocation.longitude,
            },
        };
        setDestination(selectedLocation);
        calculateTravelTimes(selectedLocation);
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", gap: "spacing.8", alignItems: "center", children: [_jsx(HouseFilledIcon, {}), _jsx(Address, { showIcon: false, fontSize: "14" })] }), _jsx(Box, { borderLeftColor: "color.surface.subdued.default", borderWidth: "borderWidth.1", borderLeftStyle: "solid", height: "sizing.56", position: "absolute", left: {
                    "breakpoint.base": "28px",
                    "breakpoint.md": "36px",
                } }), _jsxs(Box, { marginTop: "spacing.32", display: "flex", gap: "spacing.8", alignItems: "center", children: [_jsx(LocationDotFilledIcon, {}), _jsx(Controller, { name: "searchTerm", control: control, render: ({ field }) => (_jsx(Autocomplete, Object.assign({}, field, { label: t("location.map.modal.travel_times.autocomplete.label"), hasNecessityIndicator: false, items: results, emptyState: EMPTY_STATE, isLoading: isLoading, onChange: (value) => {
                                field.onChange(value);
                                setIsLoading(true);
                                setResults([]);
                                if (value === "") {
                                    setIsLoading(false);
                                    resetTravelTime();
                                }
                            }, onSubmit: handleSelectionChange }))) })] }), (loadingState === "calculating_travel_times" ||
                loadingState === "fetching_position") && (_jsx(LoadingState, { marginTop: "spacing.32" })), travelTimes &&
                (loadingState === "success" || loadingState === "failure") && (_jsx(TravelTimeIndicators, { travelTimes: travelTimes, isDistanceTooFar: isDistanceTooFar })), loadingState === "idle" && (_jsx(TextButton, { icon: LocationArrowIcon, marginTop: "spacing.32", iconPosition: "start", onPress: handleBrowserLocation, children: t("location.map.modal.travel_times.position.cta") }))] }));
};
